import * as angular from 'angular';

interface IButtonInvariant {
    clear?: boolean;
    color?: string;
    transclude?: 'replace' | 'append' | 'prepend';
    template?: string;
    handlers?: {
        [type: string]: (scope: IButtonScope, element: JQuery) => void;
    };
}

interface IButtonScope extends angular.IScope {
    type: string;
    clear: boolean;
    color: string;
    invariant: string;
    title: string;
    tooltip: string;
    open: string;
    toggle: string;
    close: boolean;
    activeInGroup: boolean;
}

class ButtonDirective implements angular.IDirective {
    readonly replace = true;
    readonly transclude = true;
    readonly restrict = 'E';
    readonly scope = {
        type: '@?',
        invariant: '@?',
        tooltip: '@?',
        open: '@?',
        toggle: '@?',
        close: '=?',
        activeInGroup: '=?'
    };

    readonly template = `
        <button
            class="fc-{{ color }}-button button small"
            title="{{ title }}"
            ng-class="{ 'fc-button-group-active': activeInGroup, 'fc-button-clear': clear, 'clear': clear }"
            ng-transclude
        ></button>
    `;

    readonly link: angular.IDirectiveLinkFn;

    constructor(private readonly clear: boolean = false) {
        this.link = this._link.bind(this);
    }

    private _link(
        scope: IButtonScope,
        element: JQuery,
        attributes: angular.IAttributes,
        _controller: angular.IController,
        transclude: angular.ITranscludeFunction
    ): void {
        // Map the invariant to a preconfigured button or just a color
        const invariant = this.mapInvariant(scope.invariant);

        scope.clear = invariant.clear || this.clear;
        scope.color = invariant.color;
        scope.title = scope.tooltip;

        attributes.$set('type', scope.type || 'button');

        // Check if the invariant defines any event bindings
        if (invariant.handlers) {
            const types = Object.keys(invariant.handlers);
            types.forEach(type => {
                const handler = invariant.handlers[type];
                element.on(type, () => handler(scope, element));
            });
        }

        transclude(() => {
            // If we have an invariant template, replace the button inner HTML
            if (invariant.template) {
                switch (invariant.transclude) {
                    case 'append': {
                        element.append(invariant.template);
                        break;
                    }

                    case 'prepend': {
                        element.prepend(invariant.template);
                        break;
                    }

                    default: {
                        element.html(invariant.template);
                        break;
                    }
                }
            }
        });

        // If a tooltip has been set add the appropriate attributes
        if (scope.tooltip) {
            attributes.$set('data-tooltip', '');
            attributes.$set('data-click-open', 'false');
        }

        // Allow capability to add the data-* attributes
        // Otherwise gets remove by directive during render
        if (scope.open) {
            attributes.$set('data-open', scope.open);
        }

        if (scope.toggle) {
            attributes.$set('data-toggle', scope.toggle);
        }

        if (scope.close) {
            attributes.$set('data-close', '');
        }
    }

    private mapInvariant(invariant: string): IButtonInvariant {
        switch (invariant) {
            case 'toggle': {
                return {
                    clear: true,
                    color: 'grey-dark',
                    transclude: 'append',
                    template: `
                        <i class="fc-button-toggle-expanded fas fa-caret-up"></i>
                        <i class="fc-button-toggle-collapsed fas fa-caret-down"></i>
                    `,
                    handlers: {
                        click: (_scope: IButtonScope, element: JQuery) => {
                            element.toggleClass('fc-button-toggled');
                        }
                    }
                };
            }

            case 'close': {
                return {
                    color: 'grey-dark',
                    template: '<i class="fas fa-times"></i>'
                };
            }

            case 'edit': {
                return {
                    color: 'blue',
                    template: '<i class="fas fa-edit"></i>'
                };
            }

            case 'remove': {
                return {
                    color: 'red',
                    template: '<i class="fas fa-trash-alt"></i>'
                };
            }

            case 'details': {
                return {
                    color: 'blue',
                    template: '<i class="fas fa-binoculars"></i>'
                };
            }

            case 'print': {
                return {
                    color: 'orange',
                    template: '<i class="fas fa-print"></i>'
                };
            }

            case 'download': {
                return {
                    color: 'green',
                    template: '<i class="fas fa-download"></i>'
                };
            }

            default: {
                return {
                    color: invariant || 'grey-dark'
                };
            }
        }
    }
}

const ButtonDirectiveFactory: angular.IDirectiveFactory = () => new ButtonDirective();

const IconButtonDirectiveFactory: angular.IDirectiveFactory = () => new ButtonDirective(true);

const LinkButtonDirectiveFactory: angular.IDirectiveFactory = () => new ButtonDirective(true);

export { ButtonDirectiveFactory, IconButtonDirectiveFactory, LinkButtonDirectiveFactory };
